import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { forceRefresh } from '@/lib/force-refresh.js';
import { logger } from '@/logger.js';
import { UpdateAvailableToast } from '@/ui/UpdateAvailableToast.js';
import { UpdateInProgressOverlay } from '@/ui/UpdateInProgressOverlay.js';

const POLL_INTERVAL_MS = 60_000;

/**
 * How long the blocking overlay may wait for the refresh navigation before giving up.
 * `forceRefresh` caps its teardown at 3s and then navigates, so a page still alive this
 * long after accepting the update means the navigation was silently blocked (an exotic
 * webview case — the same family of failures that made plain `location.reload()` a no-op
 * in Teams). The fail-safe drops the overlay so the app is never left inert forever.
 */
const NAVIGATION_FAILSAFE_MS = 15_000;

interface VersionResponse {
  sha: string;
}

async function fetchServerSha(): Promise<string | null> {
  try {
    // Compare against THIS app's own deployed build manifest — version.json carries the same
    // BUILD_SHA that vite bakes into __APP_VERSION__. The old code polled /v1/version, but that
    // returns the platform-wide release SHA (a different repo's identifier), so local !== remote
    // was ALWAYS true and the banner could never be cleared by a refresh. cache: 'no-store' so the
    // running tab sees the freshly deployed value, not its own precached copy.
    const r = await fetch('/version.json', { cache: 'no-store' });
    if (!r.ok) return null;
    const data = (await r.json()) as VersionResponse;
    return typeof data.sha === 'string' ? data.sha : null;
  } catch {
    return null;
  }
}

/**
 * Poll `/version.json` (this app's own deployed build manifest) and surface a
 * top-center banner inviting a refresh when its SHA diverges from the running
 * bundle's `__APP_VERSION__`. Both come from the same BUILD_SHA, so a refresh
 * that loads the new bundle makes them converge and dismisses the banner.
 * Required because Teams desktop keeps the precached SPA bundle
 * indefinitely (see lib/force-refresh.ts).
 *
 * Uses a plain React portal at z-60 — above Radix dialog overlays
 * (z-50) — instead of sonner. Sonner's portal layering was unreliable
 * when a modal was open: the toast ended up sitting behind the
 * dialog's scrim. Mirrors the admin-panel implementation.
 *
 * Once the user accepts the update, the refresh path (SW + cache
 * teardown, then a cache-busted navigation) keeps the old page alive
 * and interactive for several seconds, so the toast is swapped for a
 * full-screen blocking `UpdateInProgressOverlay` until the navigation
 * lands. If the refresh fails, the overlay drops and the toast returns.
 *
 * Returns the JSX banner; mount the result somewhere in the tree.
 * `null` when there's nothing to show.
 */
async function refreshNow(): Promise<void> {
  // In dev, Vite bakes __APP_VERSION__ at server start, so a plain reload
  // would re-load the same stale SHA. Trigger our `/__vite_restart` plugin
  // first; the endpoint kicks `server.restart()` and the client waits long
  // enough for the new server to come back before reloading.
  if (import.meta.env.DEV) {
    try {
      await fetch('/__vite_restart', { method: 'POST' });
    } catch {
      // restart endpoint missing or refused — fall through to plain reload
    }
    await new Promise((r) => setTimeout(r, 1500));
    window.location.reload();
    return;
  }
  await forceRefresh();
}

export function useVersionCheck(): ReturnType<typeof createPortal> | null {
  const [available, setAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const local = __APP_VERSION__;
    if (!local || local === 'unknown') return;
    let cancelled = false;

    const check = async (): Promise<void> => {
      const remote = await fetchServerSha();
      if (cancelled || !remote || remote === local) return;
      logger.info(`Version mismatch: local=${local} remote=${remote}`);
      setAvailable(true);
    };

    void check();
    const id = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  /**
   * The overlay only covers the app visually — Tab and shortcuts could still reach
   * the controls behind it. While the update runs, the app root is made `inert` so
   * keyboard focus and assistive tech are locked out too; the overlay itself lives
   * in a separate `document.body` portal and stays unaffected.
   */
  useEffect(() => {
    if (!updating) return;
    const appRoot = document.getElementById('root');
    if (!appRoot) return;
    appRoot.setAttribute('inert', '');
    return () => appRoot.removeAttribute('inert');
  }, [updating]);

  /**
   * Fail-safe for a silently blocked navigation: `refreshNow()` can resolve without the
   * page ever leaving (nothing rejects), which would leave the overlay + inert root up
   * forever. If the page is still alive after `NAVIGATION_FAILSAFE_MS`, drop the overlay —
   * `available` is still true, so the update toast comes back. The timer is cleared
   * whenever `updating` flips off (explicit failure path) or the hook unmounts.
   */
  useEffect(() => {
    if (!updating) return;
    const failSafeId = window.setTimeout(() => {
      logger.warn(
        'Update navigation never happened; dropping the blocking overlay',
      );
      setUpdating(false);
    }, NAVIGATION_FAILSAFE_MS);
    return () => window.clearTimeout(failSafeId);
  }, [updating]);

  if (typeof document === 'undefined') return null;
  if (updating) {
    return createPortal(<UpdateInProgressOverlay />, document.body);
  }
  if (!available) return null;
  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 top-4 z-[60] flex justify-center px-4"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto">
        <UpdateAvailableToast
          onRefresh={() => {
            setUpdating(true);
            refreshNow().catch((err: unknown) => {
              logger.warn(
                'Refresh failed; dropping the overlay so the toast returns',
                err,
              );
              setUpdating(false);
            });
          }}
          onDismiss={() => setAvailable(false)}
        />
      </div>
    </div>,
    document.body,
  );
}
